export class AuthError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "AuthError";
    this.code = code;
  }
}

export class AuthCredentialsError extends AuthError {
  constructor(message = "Invalid credentials.") {
    super("AUTH_CREDENTIALS", message);
    this.name = "AuthCredentialsError";
  }
}

export class AuthProfileError extends AuthError {
  constructor(message = "Failed to load user profile.") {
    super("AUTH_PROFILE", message);
    this.name = "AuthProfileError";
  }
}

export class AuthSessionError extends AuthError {
  constructor(message = "Session is no longer valid.") {
    super("AUTH_SESSION", message);
    this.name = "AuthSessionError";
  }
}

export class TokenVersionMismatchError extends AuthError {
  constructor(message = "Session was invalidated. Please sign in again.") {
    super("AUTH_TOKEN_VERSION", message);
    this.name = "TokenVersionMismatchError";
  }
}

export class AuthNetworkError extends AuthError {
  constructor(message = "Network error. Please check your connection and try again.") {
    super("AUTH_NETWORK", message);
    this.name = "AuthNetworkError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}

function extractDataMessages(data: unknown): string[] {
  if (!Array.isArray(data)) return [];

  return data
    .map((item) => {
      if (typeof item === "string") return item;
      if (!isRecord(item)) return null;
      return firstString(
        item.errorMessage,
        item.message,
        item.responseMessage,
        item.description
      );
    })
    .filter((message): message is string => Boolean(message));
}

function humanizeCode(code: string): string {
  const normalized = code.trim().toUpperCase();
  const knownMessages: Record<string, string> = {
    OTP_INVALID: "Invalid OTP.",
    OTP_EXPIRED: "OTP has expired. Please request a new OTP.",
    OTP_LOCKED: "Too many invalid OTP attempts. Please request a new OTP.",
  };

  return (
    knownMessages[normalized] ??
    normalized
      .toLowerCase()
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ")
  );
}

export function extractApiErrorMessageFromData(data: unknown): string | null {
  if (!isRecord(data)) return null;

  const detailMessages = extractDataMessages(data.data);
  if (detailMessages.length > 0) {
    return Array.from(new Set(detailMessages)).join("\n");
  }

  if (isRecord(data.error)) {
    const errorMessage = firstString(
      data.error.message,
      data.error.errorMessage,
      data.error.responseMessage,
      data.error.description
    );
    if (errorMessage) return errorMessage;

    const errorCode = firstString(data.error.code);
    if (errorCode) return humanizeCode(errorCode);
  }

  return firstString(data.message, data.responseMessage, data.title, data.error);
}

/** Extract a user-visible message from auth or API errors. */
export function extractAuthErrorMessage(error: unknown): string {
  if (error instanceof AuthError) return error.message;
  if (isRecord(error)) {
    const err = error as {
      response?: { data?: unknown };
      message?: string;
    };
    const apiMessage = extractApiErrorMessageFromData(err.response?.data);
    if (apiMessage) return apiMessage;
    if (err.message) return err.message;
  }
  return "Something went wrong. Please try again.";
}
